import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const Row = ({ it, i, busy, set }) => {
  const lift = it.look === "lift";
  return (
    <li data-testid={`other-look-item-${i}`} className="flex items-center gap-3 border-t border-stone-100 py-1.5 text-sm">
      <span className="min-w-0 flex-1 truncate text-[#2B2A26]">{it.name} <span className="text-xs text-stone-400">· {it.type}</span></span>
      <span data-testid={`other-look-status-${i}`} className={`text-xs ${lift ? "text-[#9A7B2C] font-bold" : "text-stone-500"}`}>{lift ? "New look" : "Classic"}</span>
      <button data-testid={`other-look-toggle-${i}`} disabled={busy} onClick={() => set(it, lift ? "classic" : "lift")} className="text-xs font-bold text-[#9A7B2C] hover:underline disabled:opacity-50">
        {lift ? "Back to classic" : "Try new look"}
      </button>
    </li>
  );
};

export const OtherLookPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/looks/other").then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r.data)); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const set = (it, look) => run(() => http.post(`/looks/other/${it.kind}/${it.id}`, { look }), () => `${it.name}: ${look === "lift" ? "new look on — its PDF is rebuilding" : "back to classic"}`);
  const all = (look) => run(() => http.post("/looks/other/apply-all", { look }), (x) => `${look === "lift" ? "New" : "Classic"} look set on ${x.items} item(s) and for every new one`);
  const items = d?.items || [];
  if (!items.length) return null;
  const lifted = items.filter((x) => x.look === "lift").length;
  return (
    <section data-testid="other-look-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Palette size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Guides, checklists, itineraries &amp; bundles look</b>
        <span data-testid="other-look-summary" className="text-sm text-stone-500">Cover emblem, numbered headings, tinted checklists, timeline steps and pull-quote tips · {lifted} of {items.length} on the new look</span>
        <div className="ml-auto flex gap-2">
          {busy && <Loader2 size={15} className="animate-spin text-[#9A7B2C]" />}
          <button data-testid="other-look-apply-all-btn" disabled={busy || lifted === items.length} onClick={() => all("lift")} className="rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">Use new look on all</button>
          {lifted > 0 && <button data-testid="other-look-revert-all-btn" disabled={busy} onClick={() => all("classic")} className="rounded-sm border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-50">All back to classic</button>}
        </div>
      </div>
      <ul className="mt-2 max-h-56 overflow-y-auto">{items.map((it, i) => <Row key={it.id} it={it} i={i} busy={busy} set={set} />)}</ul>
    </section>
  );
};
